import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

// // Utils imports
import TimeManager from '@/utils/timeManager';
import { logDebug, logInfo } from '@/utils/logger';

export const useTimeStore = defineStore('time', () => {
  const timeManagerInstance = new TimeManager();
  const timeTracker = ref({
    timeManager: timeManagerInstance,
  });

  // // Time Manager flags (Booleans, Nulls, & Undefineds)
  const isTimeManagerActive = ref(false);

  // // Time Manager figures (Numbers)
  const startTimeStamp = ref(null);
  const curTimeStamp = ref(null);

  // Timer visibility state for CSS animations (minimizes re-renders)
  const timerVisibilityState = ref({
    shouldShow: false,
    animationState: 'hidden', // 'hidden', 'fading-in', 'visible', 'fading-out'
  });

  // Spoof timer interval for local testing
  const spoofTimerInterval = ref(null);

  // // Configurable timer parameters (can be customized by components)
  const timerConfig = ref({
    fadeInStart: 3, // seconds - when to start fading in
    fadeInEnd: 5, // seconds - when fade in completes
    fadeOutStart: 23, // seconds - when to start fading out
    fadeOutEnd: 25, // seconds - when fade out completes
  });

  // // Time Manager text (Strings)
  // // Time Manager lists (Arrays)
  // // Time Manager dictionaries (Objects)

  // // Actions

  const initializeTimeManager = () => {
    if (!isTimeManagerActive.value) {
      // Set up the callback for time updates
      setupTimeUpdateListener();
      timeTracker.value.timeManager.attachCurrentPositionListener();
      isTimeManagerActive.value = true;

      logInfo('initializeTimeManager() Time manager initialized and listener attached');
    }
  };

  const deactivateTimeManager = () => {
    if (isTimeManagerActive.value) {
      timeTracker.value.timeManager.detachCurrentPositionListener();
      isTimeManagerActive.value = false;

      logInfo('deactivateTimeManager() Time manager deactivated and listener detached');
    }
  };

  const updateTimeFromManager = () => {
    const manager = timeTracker.value.timeManager;
    startTimeStamp.value = manager.startTimeStamp;
    curTimeStamp.value = manager.curTimeStamp;

    // logInfo(`updateTimeFromManager() Update time from manager: ${startTimeStamp.value} ${curTimeStamp.value}`);
  };

  const blankTimer = () => {
    startTimeStamp.value = null;
    curTimeStamp.value = null;
    timerVisibilityState.value = {
      shouldShow: false,
      animationState: 'hidden',
    };
  };

  const updateTimerConfig = (newConfig) => {
    logDebug(`updateTimerConfig called with: ${JSON.stringify(newConfig)}`);
    timerConfig.value = { ...timerConfig.value, ...newConfig };
    // Recalculate visibility state with new config
    updateTimerVisibilityState();

    logInfo(`updateTimerConfig() Update timer config: ${JSON.stringify(newConfig)}`);
  };

  const getTimerVisibility = (customConfig = null) => {
    const config = customConfig || timerConfig.value;

    if (!hasValidTimeData.value) {
      return {
        shouldShow: false,
        animationState: 'hidden',
      };
    }

    const timeDelta = timeDeltaFromStart.value;
    let shouldShow = false;
    let animationState = 'hidden';

    // Determine visibility and animation state based on time ranges
    if (timeDelta < config.fadeInStart) {
      shouldShow = false;
      animationState = 'hidden';
    } else if (timeDelta >= config.fadeInStart && timeDelta < config.fadeInEnd) {
      shouldShow = true;
      animationState = 'fading-in';
    } else if (timeDelta >= config.fadeInEnd && timeDelta <= config.fadeOutStart) {
      shouldShow = true;
      animationState = 'visible';
    } else if (timeDelta > config.fadeOutStart && timeDelta <= config.fadeOutEnd) {
      shouldShow = true;
      animationState = 'fading-out';
    } else {
      shouldShow = false;
      animationState = 'hidden';
    }

    return {
      shouldShow,
      animationState,
    };
  };

  const updateTimerVisibilityState = () => {
    const visibility = getTimerVisibility();

    // Only update if visibility state actually changed to prevent unnecessary re-renders
    const currentState = timerVisibilityState.value;
    if (
      currentState.shouldShow !== visibility.shouldShow ||
      currentState.animationState !== visibility.animationState
    ) {
      timerVisibilityState.value = {
        shouldShow: visibility.shouldShow,
        animationState: visibility.animationState,
      };
    }
  };

  // Local testing methods
  const startSpoofTimer = (intervalMs = 500) => {
    // Stop any existing spoof timer to prevent multiple intervals
    stopSpoofTimer();

    logDebug('Starting spoof timer for local testing (Tizen is false)');

    // Initialize spoof timer
    timeTracker.value.timeManager.spoofUpdate(0);

    // Start spoof updates
    const spoofInterval = setInterval(() => {
      timeTracker.value.timeManager.spoofUpdate(0.5); // Add 0.5 seconds every 500ms
      updateTimeFromManager();
      updateTimerVisibilityState(); // Update visibility state after each time update
    }, intervalMs);

    // Store interval ID for cleanup
    spoofTimerInterval.value = spoofInterval;

    return spoofInterval;
  };

  const stopSpoofTimer = () => {
    if (spoofTimerInterval.value) {
      clearInterval(spoofTimerInterval.value);
      spoofTimerInterval.value = null;
      logDebug('Stopped spoof timer');
    }
  };

  // // Computed properties

  const timeDeltaFromStart = computed(() => {
    if (startTimeStamp.value !== null && curTimeStamp.value !== null) {
      const delta = curTimeStamp.value - startTimeStamp.value;
      return delta;
    }
    return 0;
  });

  const hasValidTimeData = computed(() => {
    const valid = startTimeStamp.value !== null && curTimeStamp.value !== null;
    return valid;
  });

  // // Getters

  // Function to handle time updates from the time manager
  const handleTimeUpdate = () => {
    updateTimeFromManager();
    updateTimerVisibilityState();
  };

  // Set up listener for time updates instead of polling
  const setupTimeUpdateListener = () => {
    // Add a callback to the time manager to be called when currentPosition updates
    timeTracker.value.timeManager.timeUpdateCallback = handleTimeUpdate;
  };

  return {
    timeTracker,
    isTimeManagerActive,
    startTimeStamp,
    curTimeStamp,
    timeDeltaFromStart,
    hasValidTimeData,
    timerConfig,
    timerVisibilityState,
    initializeTimeManager,
    deactivateTimeManager,
    updateTimeFromManager,
    blankTimer,
    updateTimerConfig,
    getTimerVisibility,
    updateTimerVisibilityState,
    startSpoofTimer,
    stopSpoofTimer,
  };
});
